import Seo from "@/components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import PageMasthead from "@/components/PageMasthead";
import { Stagger, StaggerItem } from "@/components/motion";
import { ArrowUpRight } from "lucide-react";
import useApi from "@/hooks/useApi";
import { brandSlugByName, BRAND_DETAILS } from "@/content/brands";

const BrandRow = ({ b, index }) => {
  const slug = brandSlugByName[b.name];
  const details = slug ? BRAND_DETAILS[slug] : null;
  const hero_images = (b.hero_images?.length && b.hero_images) || details?.hero_images;
  const hero_image = b.hero_image || details?.hero_image;
  const heroImage = (hero_images && hero_images[0]) || hero_image || b.image;
  const inner = (
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${index % 2 === 1 ? "" : ""}`}>
      <div className={`lg:col-span-6 ${index % 2 === 1 ? "lg:order-2" : ""}`}>
        <div className="ed-img aspect-[16/10] rounded-2xl">
          <img src={heroImage || undefined} alt={b.name} />
        </div>
      </div>
      <div className={`lg:col-span-6 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
        <div className="flex items-center gap-3 mb-4">
          <span className="kicker">{b.category}</span>
          {b.status === "coming_soon" && (
            <span className="font-sans text-[10px] font-extrabold uppercase tracking-wide bg-primary text-primary-foreground px-2.5 py-1 rounded-full">
              Coming Soon
            </span>
          )}
        </div>
        {b.logo ? (
          <div className="inline-flex items-center mb-5">
            <img src={b.logo} alt={b.name} className={`w-auto object-contain ${b.name === "EdinformaPlus" ? "h-6 md:h-8" : "h-12 md:h-16"}`} />
          </div>
        ) : (
          <h3 className="headline text-4xl md:text-5xl uppercase text-ink mb-3 group-hover:text-primary transition-colors">
            {b.name}
          </h3>
        )}
        <p className="text-lg leading-relaxed text-foreground/80 max-w-2xl line-clamp-4 mt-4">{b.description}</p>
        <span className="mt-6 inline-flex items-center gap-2 font-sans text-sm font-bold text-ink group-hover:text-primary transition-colors">
          Explore {b.name} <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </span>
      </div>
    </div>
  );

  const wrap = "group block py-12 md:py-16 border-t border-foreground/12";
  return (
    <Link to={slug ? `/brands/${slug}` : "/brands"} className={wrap} data-testid={`brand-card-${b.name}`}>
      {inner}
    </Link>
  );
};

const Brands = () => {
  const { data } = useApi("/brands");
  const list = data || [];
  return (
    <div data-testid="brands-page">
      <Seo
        title="Our Brands"
        path="/brands"
        description="Six specialist education brands under Which Media — WhichSchoolAdvisor.com, SchoolsCompared.com, Top Schools Awards, EdinformaPlus, Education Intelligence Group and the up-and-coming WhichUniversityAdvisor.com."
      />
      <PageMasthead
        kicker="Our Brands"
        titleLines={[{ text: "A family of" }, { text: "trusted", accent: true }, { text: "education brands" }]}
        standfirst="From a parent's first school search to sector-defining data and the region's most prestigious awards, each Which Media brand serves a distinct audience with the same independent rigour."
      />
      <section className="mx-auto max-w-[1400px] px-5 md:px-8 pt-4 pb-20">
        <Stagger>
          {list.map((b, i) => (
            <StaggerItem key={b.id}>
              <BrandRow b={b} index={i} />
            </StaggerItem>
          ))}
        </Stagger>
      </section>
    </div>
  );
};

export default Brands;
